import * as apiValues from '../constants/apiValues';
import * as helpers from '../helpers';
import * as mHeaderTabActions from '../actions/mHeaderTabActions';
import PropTypes from 'prop-types';
import React from 'react';
import SuperLink from './SuperLink';
import {bindActionCreators} from 'redux';
import {connect} from 'react-redux';
import {withRouter} from '../withRouterCompat';


const MOUSE_LEAVE_DELAY_MS = 300;

function isFocusWithinTab(tabRoot){
  if(!tabRoot || typeof document === 'undefined') return false;
  const active = document.activeElement;
  return active && tabRoot.contains(active);
}

function isHeaderTabBarTarget(el){
  if(!el || typeof el.closest !== 'function') return false;
  return Boolean(
    el.closest('.m-header__tabs-middle .m-header__tab') ||
    el.closest('.m-header__tab--search') ||
    el.closest('.m-header__language')
  );
}

function isHeaderTabBarHovered(){
  if(typeof document === 'undefined') return false;
  return Boolean(document.querySelector(
    '.m-header__tabs-middle .m-header__tab:hover, .m-header__tab--search:hover, .m-header__language:hover',
  ));
}

function isFocusInPrimaryNav(){
  if(typeof document === 'undefined') return false;
  return isHeaderTabBarTarget(document.activeElement);
}

function isPointerInPrimaryNav(relatedTarget){
  return isHeaderTabBarTarget(relatedTarget);
}


class MHeaderTab extends React.Component{
  state = {
    ariaExpanded: false,
    labelFocused: false,
  };

  _tabRootRef = React.createRef();
  _labelBlurTimer = null;
  _mouseLeaveTimer = null;


  componentDidMount(){
    this.props.actions.initMHeaderTab(this.props.id);
  }

  componentDidUpdate(prevProps){
    if(!this.props.mobileFlyoutOnActivateOnly &&
       prevProps.ariaExpanded &&
       !this.props.ariaExpanded){
      this.setState({ariaExpanded: false, labelFocused: false});
    }
  }

  componentWillUnmount(){
    window.clearTimeout(this._labelBlurTimer);
    window.clearTimeout(this._mouseLeaveTimer);
  }


  isMenuOpen(){
    if(this.props.mobileFlyoutOnActivateOnly){
      return Boolean(this.props.mHeaderTab?.isOpen);
    }
    const ariaExpanded = typeof this.props.ariaExpanded !== 'undefined'
      ? this.props.ariaExpanded
      : this.state.ariaExpanded;
    return Boolean(ariaExpanded || this.state.labelFocused);
  }

  getTriggerId(){
    return `trigger-${this.props.id}`;
  }

  getSubmenuId(){
    return `submenu-${this.props.id}`;
  }

  getTriggerAriaProps(){
    const menuOpen = this.isMenuOpen();
    return {
      id: this.getTriggerId(),
      'aria-expanded': menuOpen,
      'aria-controls': this.getSubmenuId(),
    };
  }

  closeLocalMenuState(){
    this.setState({ariaExpanded: false, labelFocused: false});
  }

  scheduleDesktopMouseLeave(e){
    window.clearTimeout(this._mouseLeaveTimer);
    this._mouseLeaveTimer = window.setTimeout(() => {
      if(isFocusWithinTab(this._tabRootRef.current)){
        return;
      }
      // Pointer may have crossed a gap onto translate/search during the delay.
      if(isHeaderTabBarHovered()){
        return;
      }
      this.closeLocalMenuState();
      if(this.props.onMouseLeave){
        this.props.onMouseLeave(e);
      }
    }, MOUSE_LEAVE_DELAY_MS);
  }

  onMouseEnter = (e) => {
    if(this.props.mobileFlyoutOnActivateOnly){
      return;
    }
    window.clearTimeout(this._mouseLeaveTimer);
    this.setState({ariaExpanded: true});
    if(this.props.onMouseEnter) this.props.onMouseEnter(e);
  };

  onMouseLeave = (e) => {
    if(this.props.mobileFlyoutOnActivateOnly){
      if(isFocusWithinTab(this._tabRootRef.current)){
        return;
      }
      if(isPointerInPrimaryNav(e.relatedTarget)){
        return;
      }
      this.closeLocalMenuState();
      if(this.props.onMouseLeave) this.props.onMouseLeave(e);
      return;
    }

    if(isFocusWithinTab(this._tabRootRef.current)){
      return;
    }
    if(isPointerInPrimaryNav(e.relatedTarget)){
      return;
    }
    this.scheduleDesktopMouseLeave(e);
  };

  onTabClick = (e) => {
    if(this.props.onTabClick){
      this.props.onTabClick(e);
      return;
    }
    if(this.props.mobileFlyoutOnActivateOnly){
      this.props.actions.tabToggle(this.props.id, !this.props.mHeaderTab.isOpen);
      this.setState({ariaExpanded: !this.props.mHeaderTab.isOpen});
    }
  };

  onMobileTriggerKeyDown = (e) => {
    if(e.key === 'Enter' || e.key === ' '){
      e.preventDefault();
      this.onTabClick(e);
    }
  };

  onDesktopTriggerClick = (e) => {
    if(this.props.mobileFlyoutOnActivateOnly || this.props.searchTrigger){
      return;
    }
    e.preventDefault();
    if(this.props.onTriggerToggle){
      this.props.onTriggerToggle(this.props.id, e);
    }
  };

  onTriggerKeyDown = (e) => {
    if(this.props.searchTrigger){
      this.onSearchLabelKeyDown(e);
      return;
    }

    if(e.key === 'Enter' || e.key === ' '){
      e.preventDefault();
      if(this.props.mobileFlyoutOnActivateOnly){
        return;
      }
      this.onDesktopTriggerClick(e);
      return;
    }

    if(e.key === 'Escape' && this.isMenuOpen() && !this.props.mobileFlyoutOnActivateOnly){
      e.preventDefault();
      if(this.props.onTriggerDismiss){
        this.props.onTriggerDismiss(this.props.id);
      }
    }
  };

  onToggleClick = () => {
    if(!this.props.mobileFlyoutOnActivateOnly){
      return;
    }
    this.props.actions.tabToggle(this.props.id, !this.props.mHeaderTab.isOpen);
    this.setState({ariaExpanded: !this.props.mHeaderTab.isOpen});
  };

  onTabFocusIn = (e) => {
    const root = this._tabRootRef.current;
    if(!root || !root.contains(e.target)){
      return;
    }
    if(e.target.closest('.m-header__tab-toggle')){
      return;
    }
    // Search opens via explicit click/Enter on the trigger. Focusing the icon alone
    // (e.g. after Escape/close) must not reopen the overlay over page content.
    if(this.props.searchTrigger && !e.target.closest('.m-header__tab-menu')){
      return;
    }
    if(this.props.mobileFlyoutOnActivateOnly){
      if(e.target.closest('.m-header__tab-link--mobile')){
        return;
      }
      if(e.target.closest('.m-header__tab-menu')){
        return;
      }
    }

    window.clearTimeout(this._mouseLeaveTimer);
    this.setState({labelFocused: true, ariaExpanded: true});
    if(this.props.onMouseEnter){
      const trigger = root.querySelector('.m-header__tab-link');
      this.props.onMouseEnter({
        ...e,
        currentTarget: trigger || root,
      });
    }
  };

  onTabFocusOut = () => {
    this.scheduleLabelFocusCheck();
  };

  scheduleLabelFocusCheck(){
    window.clearTimeout(this._labelBlurTimer);
    this._labelBlurTimer = window.setTimeout(() => {
      if(isFocusWithinTab(this._tabRootRef.current)){
        return;
      }
      if(isFocusInPrimaryNav()){
        this.setState({labelFocused: false, ariaExpanded: false});
        return;
      }
      this.setState({labelFocused: false, ariaExpanded: false});
      if(this.props.onMouseLeave){
        this.props.onMouseLeave();
      }
    }, 0);
  };

  onSearchLabelKeyDown = (e) => {
    if(e.key === 'Enter' || e.key === ' '){
      e.preventDefault();
      if(this.props.onTabClick) this.props.onTabClick(e);
    }
  };

  onUrlTabKeyDown = (e) => {
    if(e.key === 'Escape' && this.isMenuOpen() && !this.props.mobileFlyoutOnActivateOnly){
      e.preventDefault();
      if(this.props.onTriggerDismiss){
        this.props.onTriggerDismiss(this.props.id);
      }
    }
  };

  renderTabLabel(){
    const triggerAria = this.getTriggerAriaProps();

    if(this.props.searchTrigger){
      const searchLabel = (this.props.title && String(this.props.title).trim()) || 'Search';
      return (
        <button type="button"
                className="m-header__tab-link"
                aria-label={searchLabel}
                onClick={this.props.onTabClick}
                onKeyDown={this.onSearchLabelKeyDown}
                {...triggerAria}>
          <span className="visually-hidden">{searchLabel}</span>
        </button>
      );
    }

    if(this.props.url && !this.props.mobileFlyoutOnActivateOnly){
      return (
        <SuperLink to={this.props.url}
                   className="m-header__tab-link"
                   onKeyDown={this.onUrlTabKeyDown}
                   aria-haspopup="true"
                   {...triggerAria}>
          {this.props.title}
        </SuperLink>
      );
    }

    return (
      <button type="button"
              className="m-header__tab-link"
              onClick={this.onDesktopTriggerClick}
              onKeyDown={this.onTriggerKeyDown}
              {...triggerAria}>
        {this.props.title}
      </button>
    );
  }

  render(){
    if(!this.props.mHeaderTab) return null;

    const bgUrl = this.props.bgPath ? apiValues.imgPath(this.props.bgPath) : '';
    const tabStyle = bgUrl
      ? {
          backgroundImage: `url(${bgUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center top',
          backgroundRepeat: 'no-repeat',
        }
      : {};
    const openClass = this.props.mHeaderTab.isOpen ? 'is-open' : '';
    const normalizedUrl = this.props.url && String(this.props.url).replace(/^\//, '');
    const currentPath = typeof window !== 'undefined'
      ? window.location.pathname.replace(/^\//, '')
      : '';
    const activeClass = normalizedUrl &&
      (normalizedUrl === currentPath || currentPath.startsWith(`${normalizedUrl}/`))
      ? 'is-active'
      : '';
    const menuOpen = this.isMenuOpen();
    const keyboardClass = this.state.labelFocused ? 'm-header__tab--label-focus' : '';
    const navActiveClass = menuOpen ? 'm-header__tab--nav-active' : '';
    const ariaExpanded = menuOpen;

    const tabLink = this.renderTabLabel();
    const subheadTitle = this.props.url ? (
      <SuperLink to={this.props.url} className="m-header__subhead-title">{this.props.title}</SuperLink>) : (
                         <span className="m-header__subhead-title">{this.props.title}</span>);
    const isMobileSearchDialog = this.props.id === 'search' &&
      menuOpen &&
      typeof window !== 'undefined' &&
      window.innerWidth <= 991;

    const submenuRole = isMobileSearchDialog ? 'dialog' : (menuOpen ? 'region' : undefined);
    const submenuLabelledBy = menuOpen && !isMobileSearchDialog ? this.getTriggerId() : undefined;
    const mobileSiblingFlyoutActive = Boolean(
      this.props.mobileSubmenuOpen &&
      this.props.mobileFlyoutOnActivateOnly &&
      !this.props.mHeaderTab.isOpen,
    );
    const mobileFlyoutOpen = Boolean(
      this.props.mobileFlyoutOnActivateOnly &&
      this.props.mHeaderTab.isOpen,
    );

    return (
      <div ref={this._tabRootRef}
           className={`m-header__tab ${this.props.className || ''} ${openClass} ${activeClass} ${keyboardClass} ${navActiveClass}`}
           onMouseEnter={this.onMouseEnter}
           onMouseLeave={this.onMouseLeave}
           onFocusCapture={this.onTabFocusIn}
           onBlurCapture={this.onTabFocusOut}
           {...(mobileSiblingFlyoutActive ? {inert: ''} : {})}>
        {isMobileSearchDialog && React.isValidElement(tabLink)
          ? React.cloneElement(tabLink, {tabIndex: -1, 'aria-hidden': true})
          : tabLink}
        {this.props.title ? (
          <button aria-expanded={ariaExpanded}
                  className="m-header__tab-toggle"
                  onClick={this.onToggleClick}
                  tabIndex={-1}>
            Toggle {this.props.title} submenu
          </button>
        ) : null}
        {this.props.title && !this.props.searchTrigger ? (
          <button type="button"
                  className="m-header__tab-link m-header__tab-link--mobile"
                  id={`${this.getTriggerId()}-mobile`}
                  aria-expanded={ariaExpanded}
                  aria-controls={this.getSubmenuId()}
                  tabIndex={mobileFlyoutOpen ? -1 : undefined}
                  onClick={this.onTabClick}
                  onKeyDown={this.onMobileTriggerKeyDown}>{this.props.title}</button>
        ) : null}
        <ul className="m-header__tab-menu"
            id={this.getSubmenuId()}
            role={submenuRole}
            aria-modal={isMobileSearchDialog ? 'true' : undefined}
            aria-label={isMobileSearchDialog ? 'Search our projects' : undefined}
            aria-labelledby={submenuLabelledBy}
            {...(menuOpen ? {} : {inert: ''})}>
          <li className="m-header__tab-menu-shell">
            <div className="m-header__tab-menu-content" style={tabStyle}>
              <div className="m-header__subhead">
                <button type="button" className="m-header__subhead-back" onClick={this.onTabClick}>Back</button>
                {subheadTitle}
              </div>
              {this.props.children}
            </div>
          </li>
        </ul>
      </div>
    );
  }
}


export default withRouter(connect(
  (state, ownProps) => ({
    mHeaderTab: helpers.getById(state.mHeaderTab, ownProps.id),
  }),
  (dispatch) => ({
    actions: bindActionCreators(mHeaderTabActions, dispatch),
  }),
)(MHeaderTab));


MHeaderTab.propTypes = {
  actions: PropTypes.object.isRequired,
  ariaExpanded: PropTypes.bool,
  bgPath: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
  id: PropTypes.string,
  mobileFlyoutOnActivateOnly: PropTypes.bool,
  mobileSubmenuOpen: PropTypes.bool,
  mHeaderTab: PropTypes.object,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onTabClick: PropTypes.func,
  onTriggerDismiss: PropTypes.func,
  onTriggerToggle: PropTypes.func,
  searchTrigger: PropTypes.bool,
  title: PropTypes.string,
  url: PropTypes.string,
};
